import React, { useEffect, useState, useRef } from 'react';
import ChatInput from '../components/sub-components/ChatInput';
import Sidebar from '../components/sub-components/Sidebar';
import { createChatHistory, updateHistoryName } from '../Api/req';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { fetchChatHistory, clearChatHistory } from '../slice/chatSlice';
import ChatBlob from '../components/atomic/ChatBlob';
import { useParams } from 'react-router-dom';
import constants from '../components/constants';
import DropDown from '../components/atomic/DropDown';
import { fetchUserFiles } from '../slice/apiSlice';
import { clearUserFiles } from '../slice/apiSlice';
import axios from 'axios';
import HomeTabsInvisible from '../components/sub-components/HomeTabsInvisible';
import ChatZoomControls from '../components/sub-components/ChatZoomControls';
import { useChatZoom } from '../hooks/useChatZoom';
import { authHeader } from '../utils/authHeaders';

const Chat = () => {
  const { id: routeHistoryId } = useParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Collapsed by default
  const [sidebarWidth, setSidebarWidth] = useState(445);
  const [historyId, setHistoryId] = useState(routeHistoryId || null);
  const [taskId, setTaskId] = useState(null); // Always null for chat-only
  const [chatName, setChatName] = useState("");
  const chatContainerRef = useRef(null);
  const chatZoom = useChatZoom(chatContainerRef);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [chatSettings, setChatSettings] = useState(null);

  const { userFiles, userFilesStatus, userFilesError } = useSelector((state) => state.api);

  const userId = localStorage.getItem('id');
  const dispatch = useDispatch();
  const chatHistory = useSelector(state => state.chat.chatHistory);
  const loading = useSelector(state => state.chat.loading);
  const name = localStorage.getItem('name');

  // Clear chat history on mount (so only welcome message is shown)
  useEffect(() => {
    dispatch(clearChatHistory());
    dispatch(clearUserFiles());
  }, [dispatch]);

  // Always fetch chat history if route param is present
  useEffect(() => {
    if (routeHistoryId && userId) {
      setHistoryId(routeHistoryId);
      const CHAT_TASK_ID = "40";
      setTaskId(CHAT_TASK_ID); // Always chat task id for chat-only
      dispatch(fetchChatHistory({ historyId: routeHistoryId, userId, taskId: CHAT_TASK_ID }));
    }
  }, [routeHistoryId, userId, dispatch]);

  const { data: userData } = useSelector((state) => state.admin);

  // Always fetch files for chat by task_id 40 and historyId
  useEffect(() => {
    if (userId && historyId) {
      dispatch(fetchUserFiles({ userId, historyId, taskId: "40", organizationId: userData?.organization_id }));
    }
  }, [userId, historyId, userData?.organization_id, dispatch]);

  useEffect(() => {
    if (!historyId || !chatName) return;
    const handler = setTimeout(() => {
      updateHistoryName(historyId, chatName).catch((err) => {
        // Optionally handle error
      });
    }, 800); // 800ms debounce
    return () => clearTimeout(handler);
  }, [chatName, historyId]);

  useEffect(() => {
    // Fetch ChatSettings on mount
    axios.get('/api/settings/chat', {
      headers: { ...authHeader() },
    })
      .then(res => setChatSettings(res.data))
      .catch(() => setChatSettings(null));
  }, []);

  const handleWidthChange = (newWidth) => {
    setSidebarWidth(newWidth);
    localStorage.setItem('sidebarWidth', newWidth.toString());
  };

  const openSidebar = () => setIsSidebarOpen(true);
  const closeSidebar = () => setIsSidebarOpen(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!chatContainerRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
      // Show button if not at bottom (allow 50px leeway)
      setShowScrollButton(scrollHeight - scrollTop - clientHeight > 50);
    };
    const ref = chatContainerRef.current;
    if (ref) {
      ref.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (ref) ref.removeEventListener('scroll', handleScroll);
    };
  }, [chatContainerRef]);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  useEffect(() => {
    if (userFilesError) {
      console.error('❌ Files error:', userFilesError);
    }
  }, [userFiles, userFilesStatus, userFilesError]);

  return (
    <div className="flex h-screen bg-white">
      {/* Main chat area */}
      <div className="flex flex-col flex-grow transition-all duration-300 relative">
        {!isSidebarOpen
        // && (
          // <button
          //   onClick={openSidebar}
          //   className="absolute flex w-fullme='right' top-4 right-4 z-10 hover:opacity-80 transition-opacity duration-200"
          // >
          //   <img src={constants.fold} alt="Open Sidebar" />
          // </button>)
          
          }
        <main className="flex-grow flex flex-col items-center p-4 overflow-y-auto justify-center">
          {/* pt-10 lives on the inner wrapper below (see the message-list
              branch), not here — padding-top on this scroll container would
              be a permanent gap above the sticky Standard Question pill,
              since a scroll container's own padding never scrolls away. */}
          <div ref={chatContainerRef} className="pb-10 flex flex-col items-start justify-start gap-5 w-full chat-container max-w-4xl mx-auto overflow-y-auto"
          style={{
            minHeight: chatHistory.length > 0 ? "300px" : "0",
            maxHeight: 'calc(100vh - 220px)', // leave space for input, header, etc.
          }}
        >
          {loading && chatHistory.length === 0 ? (
            <div className="flex flex-1 items-center justify-center w-full h-full min-h-[400px]">
              <svg className="animate-spin h-12 w-12 text-custom-yellow" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          ) : chatHistory.length === 0 ? (
            <div className="flex-grow flex flex-col items-center p-4 overflow-y-auto justify-center">
              <header className="p-4">
              <h1 className="text-2xl font-bold text-center text-custom-gray"> Hallo, {name}. Womit kann ich Ihnen heute behilflich sein?</h1>
              </header>
              <HomeTabsInvisible />
            </div>
          ) : (
            <div className="pt-10 w-full flex flex-col gap-5" style={{ zoom: chatZoom.zoom }}>
              {chatHistory.map((message, index) => (
                <ChatBlob
                  key={index}
                  user={message.role}
                  message={{
                    ...message,
                    source: message.sources || []
                  }}
                  openSidebar={openSidebar}
                  taskId={taskId}
                  historyId={historyId}
                />
              ))}
              {loading && (
                <div className="flex justify-start items-center w-full">
                  <svg className="animate-spin h-8 w-8 text-custom-yellow" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
              )}
            </div>
          )}
        </div>
        {showScrollButton && (
          <button
            onClick={scrollToBottom}
            className="fixed bottom-24 left-8 bg-custom-yellow opacity-80 hover:bg-custom-yellow hover:opacity-100 text-white rounded-full p-3 shadow-lg transition-all duration-300 z-50"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-6 w-6" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M19 14l-7 7m0 0l-7-7m7 7V3" 
              />
            </svg>
          </button>
        )}
      </main>
      {chatHistory.length > 0 && <ChatZoomControls {...chatZoom} />}
      {/* Updated input area to match Analysis UI exactly */}
      <div className="pb-10">
        <div className="bg-white w-full max-w-4xl mx-auto rounded-lg ">
          <div className="flex items-center gap-4 pt-3 pr-6 pl-3 w-full justify-between">
            <div className="flex items-center gap-3 w-full">
              <div className="flex-1 min-w-0">
                <DropDown
                  list={[{ task: 'Chat' }]}
                  selectedValue="Chat"
                  onSelect={() => {}}
                  disabled={true}
                  className="w-full px-3 py-3 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white"
                />
              </div>
              <img className="w-4 h-4 flex-shrink-0" src={constants.arrow} alt="Arrow" />
              <div className="flex-1 min-w-0">
                <input
                  type="text"
                  className="w-full px-3 py-3 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white"
                  placeholder="Chat benennen (optional)"
                  value={chatName}
                  onChange={(e) => setChatName(e.target.value)}
                />
              </div>
              <button
                type="button"
                className="inline-flex gap-2 items-center px-3 py-2 text-sm leading-4 font-medium rounded-md text-custom-gray bg-white opacity-0 pointer-events-none"
                disabled
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7 20V18H17V20H7Z"
                    fill="#5F6368"
                    fillOpacity="0.8"
                  />
                  <path
                    d="M13 12.175L13 4L11 4L11 12.175L8.4 9.6L7 11L12 16L17 11L15.6 9.6L13 12.175Z"
                    fill="#5F6368"
                    fillOpacity="0.8"
                  />
                </svg>
                Export
              </button>
            </div>
          </div>
          <ChatInput
            historyId={historyId}
            setHistoryId={setHistoryId}
            taskID={taskId}
            chatName={chatName}
            setChatName={setChatName}
            userFiles={userFiles} 
            onHistoryCreated={(newHistoryId, newTaskId) => {
              setHistoryId(newHistoryId);
              setTaskId(newTaskId);
            }}
            chatSettings={chatSettings}
          />
        </div>
      </div>
    </div>
    {/* File/document sidebar remains on the right */}
    <div
      className="h-full transition-all duration-300 overflow-hidden"
      style={{ width: isSidebarOpen ? `${sidebarWidth}px` : '0' }}
    >
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={closeSidebar}
        taskID="40"
        onWidthChange={handleWidthChange}
        historyId={historyId}
        setHistoryId={setHistoryId}
        mode="chat"
      />
    </div>
  </div>
  );
};

export default Chat;